import React, { useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Line, Bar, Scatter } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import {
    Grid,
    Paper,
    Typography,
    Box,
    Alert,
    Drawer,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip as MuiTooltip,
    CircularProgress,
} from '@mui/material';
import { ChevronLeft, ZoomOutMap, CenterFocusStrong, RadioButtonChecked, RadioButtonUnchecked, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import Collapse from '@mui/material/Collapse';
import { computeFitDiagnostics, getDiagnosticChips } from '../analysis/fitDiagnostics';
import {
    fitAllDistributions,
    computeTailProbability,
    computeDistributionMoments,
    computeShapeDiagnostics,
    computeCapabilityEquivalents,
    generateConservativeRecommendation,
    normalCDF
} from '../analysis/DistributionEngine';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, BarElement, Title, Tooltip, Legend, annotationPlugin, Filler);

// Significance level for GOF tests
const ALPHA = 0.05;

// One-sided capability index (Minitab CPU/CPL, PPU/PPL). Prefer the backend-computed
// value (`provided`, from /api/capability); fall back to computing from the
// limit/mean/σ props — Number-coerced because pg/history rows may deliver them as
// strings — so it works on a history reload where the index wasn't persisted. Null
// when the limit is absent (one-sided spec), the mean is missing, or σ ≤ 0.
export const pickIdx = (provided, limit, mean, sigma, upper) => {
    if (Number.isFinite(provided)) return provided;
    // FIX-9 residual: `limit === null` (a legitimately absent one-sided limit) must
    // short-circuit BEFORE the Number() coercion below — Number(null) is 0, not NaN,
    // so without this explicit check a null limit silently fabricates a (mean-0)/(3σ)
    // index instead of returning null. Number(undefined) is already NaN and was
    // already safe; this closes the null-specific gap.
    if (limit === null || limit === undefined || limit === '') return null;
    const L = Number(limit), M = Number(mean), S = Number(sigma);
    if (!Number.isFinite(L) || !Number.isFinite(M) || !(S > 0)) return null;
    return upper ? (L - M) / (3 * S) : (M - L) / (3 * S);
};
export const fmtIdx = (v) => (Number.isFinite(v) ? v.toFixed(3) : '—');

// Stability gate (#3): resolve the unified unstable flag + signal count. Prefer
// the backend Nelson/AIAG Rules 1-4 result (a boolean `unstable` on the fresh
// /api/capability response); fall back to the client-side Rule-1 out-of-control
// count for history reloads (violations are not persisted). Pure + exported so
// the gate logic is unit-tested without rendering the whole ChartSection.
export const resolveStability = (backendUnstable, backendSignalCount, rule1Count) => {
    const hasBackend = typeof backendUnstable === 'boolean';
    const isUnstable = hasBackend ? backendUnstable : rule1Count > 0;
    const signalCount = hasBackend ? (backendSignalCount || 0) : rule1Count;
    return { isUnstable, signalCount, hasBackend };
};

// Capability confidence intervals (#4). The confidence level is user-configurable
// among the three standard SPC levels; 95% is the default. Exact two-sided
// standard-normal quantiles z_{(1+level)/2} (no PPF call needed for a fixed set).
export const Z_BY_CONFIDENCE = {
    90: 1.6448536269514722,
    95: 1.959963984540054,
    99: 2.5758293035489004,
};

// Bissell (1990) large-sample confidence interval for a capability index
// (Cpk or Ppk): index ± z · sqrt( 1/(9n) + index² / (2(n−1)) ) — the normal-theory
// approximation Minitab prints. Pure + exported for unit testing. Returns null
// when it cannot be computed (n < 2 or a non-finite index) so the UI shows "—".
export const capabilityCI = (index, n, z) => {
    if (!Number.isFinite(index) || !Number.isFinite(z) || !Number.isFinite(n) || n < 2) return null;
    const se = Math.sqrt(1 / (9 * n) + (index * index) / (2 * (n - 1)));
    return { lower: index - z * se, upper: index + z * se };
};

function ChartSection({
    title,
    dataset,
    usl,
    lsl,
    mean,
    std_dev,
    within_std_dev,
    cp,
    cpk,
    cpu,
    cpl,
    pp,
    ppk,
    ppu,
    ppl,
    ppm_overall,
    ppm_within,
    movingRanges,
    mrMean,
    i_ucl,
    i_lcl,
    mr_ucl,
    mr_lcl,
    orderedQuantiles,
    theoreticalQuantiles,
    referenceLine,
    lowerBand,
    upperBand,
    ad_value,
    p_value,
    // New props for X̄-R charts
    chartType,
    subgroupSize,
    xbarValues,
    rValues,
    xbarBar,
    rBar,
    xbar_ucl,
    xbar_lcl,
    r_ucl,
    r_lcl,
    subgroupIds,
    allValues,
    // Stability gate (#3): backend Rules 1-4 result. undefined on history reload
    // (violations are not persisted) → the component falls back to Rule-1.
    unstable,
    unstableSignalCount,
    // UX-8: true/false when the caller can tell "raw input, not yet analyzed"
    // apart from "a real computed result" (e.g. the first-visit example while its
    // auto-submit POST is in flight); undefined (other callers) preserves the old
    // behavior exactly. `submitting` distinguishes an in-flight computation from a
    // stalled/failed one so the empty state doesn't lie about which it is.
    hasComputedStats,
    submitting,
    // UX-9: a concise, sanitized copy of DataEntryTable's submit-error state
    // (lifted via CockpitPage), shown when hasComputedStats === false and
    // nothing is currently submitting — replaces the generic "stalled" copy
    // with the actual reason so a failed submit isn't invisible on mobile,
    // where the Data Entry drawer holding the full Alert is closed by
    // default. undefined/null (other callers, or no error) preserves the
    // prior generic-message behavior exactly.
    submitError,
}) {
    // FIX-9 residual: normalize usl/lsl ONCE, here, before any consumer reads them.
    // One-sided specs are legal (REQ-CAP-007/008) — a dataset may carry only usl or
    // only lsl, with the other null/undefined — and a bare 0 is also a legal limit
    // (a real LSL of 0), so it must never be treated as absent. Incoming shapes vary
    // by caller: a genuine number (fresh submit), a pg NUMERIC string like "11"
    // (history reload), or null/undefined (one-sided/absent limit). Every downstream
    // consumer below must read uslF/lslF — never the raw usl/lsl props — so an
    // absent limit stays null instead of being fabricated as 0.
    const uslF = (usl === null || usl === undefined || usl === '') ? null : (Number.isFinite(Number(usl)) ? Number(usl) : null);
    const lslF = (lsl === null || lsl === undefined || lsl === '') ? null : (Number.isFinite(Number(lsl)) ? Number(lsl) : null);

    const [selectedDistribution, setSelectedDistribution] = useState('normal');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [useSpecLimitsScale, setUseSpecLimitsScale] = useState(false); // Toggle for y-axis scale
    const [shapeDiagnosticsOpen, setShapeDiagnosticsOpen] = useState(false); // Toggle for shape diagnostics section
    const [autoModeEnabled, setAutoModeEnabled] = useState(false); // Auto (Recommended) mode
    const [dataSummaryOpen, setDataSummaryOpen] = useState(false); // Toggle for raw data summary section
    const [warningsExpanded, setWarningsExpanded] = useState(false); // Warnings collapsed by default
    // Capability CI confidence level (#4) — configurable, 95% default, remembered.
    const [confidenceLevel, setConfidenceLevel] = useState(() => {
        try {
            const v = parseInt(window.localStorage.getItem('capabilityConfidenceLevel'), 10);
            return [90, 95, 99].includes(v) ? v : 95;
        } catch { return 95; }
    });
    const handleConfidenceChange = (lvl) => {
        setConfidenceLevel(lvl);
        try { window.localStorage.setItem('capabilityConfidenceLevel', String(lvl)); } catch { /* non-fatal */ }
    };

    // Compute values for diagnostics (must be before early return for hooks rules)
    // Filter to only include valid numeric values (dataset.values may contain [subgroupId, value] pairs with nulls)
    // Memoized so the heavy downstream memos (fitAllDistributions across 10+
    // distributions, GOF, capability equivalents) keep their cache across the
    // component's own toggle-driven re-renders (drawer, y-scale, warnings, etc.).
    // Identical computation — only the array reference is stabilized.
    const values = useMemo(() => (dataset?.values
        ? dataset.values.flat().filter(v => typeof v === 'number' && !isNaN(v) && v !== null)
        : []), [dataset]);
    const isXbarRChart = chartType === 'xbar-r' || (subgroupSize && subgroupSize > 1);
    // For histograms/diagnostics, prefer allValues (all individual measurements) when available
    // Filter nulls/undefined/empty strings BEFORE Number coercion to avoid Number("")=0 artifacts
    const histValues = useMemo(() => (isXbarRChart && allValues && allValues.length > 0
        ? allValues
            .filter(v => v !== null && v !== undefined && v !== '' && (typeof v !== 'string' || v.trim() !== ''))
            .map(Number)
            .filter(v => !isNaN(v) && isFinite(v))
        : values), [isXbarRChart, allValues, values]);

    // Compute distribution-agnostic fit diagnostics (hooks must be called unconditionally)
    const fitDiagnostics = useMemo(() => computeFitDiagnostics(histValues), [histValues]);
    const diagnosticChips = useMemo(() => getDiagnosticChips(fitDiagnostics), [fitDiagnostics]);

    // Compute distribution fits for all candidate distributions (Phase 2)
    const distributionFits = useMemo(() => fitAllDistributions(histValues, ALPHA), [histValues]);

    // Get selected distribution fit result
    const selectedDistFit = useMemo(() => {
        const result = distributionFits.results?.find(r => r.id === selectedDistribution);
        return result?.canFit && result?.fit ? result : null;
    }, [distributionFits.results, selectedDistribution]);

    // Compute shape diagnostics
    const shapeDiagnostics = useMemo(() => computeShapeDiagnostics(histValues), [histValues]);

    // Determine if shape diagnostics should be shown
    // Show when: multiple acceptable fits, no acceptable fits, or any Phase 1 diagnostic triggered
    const showShapeDiagnostics = useMemo(() => {
        const acceptableCount = distributionFits.results?.filter(r => r.status === 'acceptable').length || 0;
        const hasNoAcceptable = acceptableCount === 0;
        const hasMultipleAcceptable = acceptableCount > 1;
        const hasDiagnosticChip = diagnosticChips.length > 0;
        return hasNoAcceptable || hasMultipleAcceptable || hasDiagnosticChip;
    }, [distributionFits.results, diagnosticChips]);

    // Compute tail probability and PPM for selected distribution
    const tailProbability = useMemo(() => {
        if (!selectedDistFit?.fit?.cdf) return null;

        // For Normal distribution, use sample sigma (std_dev prop, n-1 divisor) to match Overall PPM.
        // MLE sigma (n divisor) is retained in selectedDistFit.fit for ranking/AIC purposes.
        if (selectedDistFit.id === 'normal' && std_dev > 0) {
            const sampleSigmaCdf = (x) => normalCDF((x - mean) / std_dev);
            return computeTailProbability(sampleSigmaCdf, lslF, uslF);
        }

        return computeTailProbability(selectedDistFit.fit.cdf, lslF, uslF);
    }, [selectedDistFit, lslF, uslF, mean, std_dev]);

    // Compute distribution moments for selected distribution
    const distMoments = useMemo(() => {
        if (!selectedDistFit) return null;
        return computeDistributionMoments(selectedDistFit.id, selectedDistFit.params);
    }, [selectedDistFit]);

    // Generate conservative recommendation (Phase 5)
    const conservativeRec = useMemo(() =>
        generateConservativeRecommendation(distributionFits.results, ALPHA),
        [distributionFits.results]
    );

    // Compute capability equivalents for selected distribution (Phase 4.5)
    const capabilityEquivalents = useMemo(() => {
        if (!selectedDistFit?.fit?.ppf) return null;
        return computeCapabilityEquivalents(
            selectedDistFit.fit.ppf,
            selectedDistFit.fit.cdf,
            histValues,
            lslF,
            uslF,
            selectedDistFit.isUnstable
        );
    }, [selectedDistFit, histValues, lslF, uslF]);

    // Effect: Auto mode - update selected distribution based on recommendation
    React.useEffect(() => {
        if (autoModeEnabled && conservativeRec.selectedId) {
            setSelectedDistribution(conservativeRec.selectedId);
        }
    }, [autoModeEnabled, conservativeRec.selectedId]);

    // Probability plot data for selected distribution (Phase 4)
    // Must be computed before early return for hooks rules
    const selectedDistProbPlot = useMemo(() => {
        if (!selectedDistFit?.fit?.ppf) return null;
        if (!histValues || histValues.length < 3) return null;

        const sortedData = [...histValues].sort((a, b) => a - b);
        const n = sortedData.length;

        try {
            // Compute plotting positions (Hazen formula: (i - 0.5) / n)
            const plottingPositions = sortedData.map((_, i) => (i + 0.5) / n);

            // Compute theoretical quantiles using PPF
            const theoreticalQ = plottingPositions.map(p => {
                const q = selectedDistFit.fit.ppf(p);
                return isFinite(q) ? q : null;
            });

            // Filter out any null values
            const validIndices = theoreticalQ.map((q, i) => q !== null ? i : null).filter(i => i !== null);
            const filteredTheoretical = validIndices.map(i => theoreticalQ[i]);
            const filteredOrdered = validIndices.map(i => sortedData[i]);

            if (filteredTheoretical.length < 3) return null;

            // Compute reference line (regression line through theoretical vs ordered)
            const n2 = filteredTheoretical.length;
            const xMean = filteredTheoretical.reduce((a, b) => a + b, 0) / n2;
            const yMean = filteredOrdered.reduce((a, b) => a + b, 0) / n2;

            let ssXY = 0, ssXX = 0;
            for (let i = 0; i < n2; i++) {
                ssXY += (filteredTheoretical[i] - xMean) * (filteredOrdered[i] - yMean);
                ssXX += (filteredTheoretical[i] - xMean) * (filteredTheoretical[i] - xMean);
            }

            const slope = ssXX > 0 ? ssXY / ssXX : 1;
            const intercept = yMean - slope * xMean;

            const refLine = filteredTheoretical.map(x => intercept + slope * x);

            return {
                theoreticalQuantiles: filteredTheoretical,
                orderedQuantiles: filteredOrdered,
                referenceLine: refLine,
                slope,
                intercept
            };
        } catch {
            return null;
        }
    }, [selectedDistFit, histValues]);

    if (!dataset || !dataset.values || dataset.values.length === 0) {
        return (
            <Box sx={{ position: 'relative', width: '100%' }}>
                {/* Title row with nav links - always visible */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 2,
                    pb: 0.5,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 500 }}>
                        Charts
                    </Typography>
                </Box>
                <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 8 }}>
                    <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        No data available for charting. Please enter data to begin.
                    </Typography>
                </Box>
            </Box>
        );
    }

    // UX-8: dataset.values is populated but no computed stats exist yet — e.g. the
    // first-visit example, mid auto-submit. Rendering the chart below with mean/
    // std_dev hard-defaulted to 0 (CockpitPage's `|| 0` props) produces a materially
    // broken chart: the histogram collapses every point into one bin against a
    // 0-anchored axis (min = Math.min(lsl, mean-3σ, dataMin) = 0) and the normal
    // curve is NaN throughout (σ=0). Show a real loading/empty state instead of
    // fabricated statistics. `hasComputedStats === false` (not falsy) so callers
    // that don't pass the prop at all keep the prior behavior unchanged.
    if (hasComputedStats === false) {
        return (
            <Box sx={{ position: 'relative', width: '100%' }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 2,
                    pb: 0.5,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 500 }}>
                        Charts
                    </Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2} sx={{ py: 8 }}>
                    {submitting ? (
                        <>
                            <CircularProgress size={28} sx={{ color: '#90caf9' }} />
                            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                Computing analysis…
                            </Typography>
                        </>
                    ) : submitError ? (
                        <Typography variant="h6" sx={{ color: '#ff8a80' }}>
                            Analysis failed: {submitError} — open Data Entry for details
                        </Typography>
                    ) : (
                        <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            Analysis not yet available. Press Submit in Data Entry to compute it.
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }

    // Check for out-of-control points based on chart type
    let outOfControlI, outOfControlMR;
    if (isXbarRChart) {
        outOfControlI = xbarValues?.filter(v => v > xbar_ucl || v < xbar_lcl).length || 0;
        outOfControlMR = rValues?.filter(r => r > r_ucl).length || 0;
    } else {
        outOfControlI = values.filter(v => v > i_ucl || v < i_lcl).length;
        outOfControlMR = movingRanges?.filter(mr => mr > mr_ucl).length || 0;
    }
    const isNonNormal = p_value < 0.05;

    // Unified stability flag (#3, stability-before-capability gate). Both the
    // "Stable" chip and the Cp/Cpk gate read this ONE source (see resolveStability)
    // so stability has a single definition across the app.
    const { isUnstable, signalCount: stabilitySignalCount, hasBackend: hasBackendStability } =
        resolveStability(unstable, unstableSignalCount, outOfControlI);

    // Capability confidence intervals (#4). Computed client-side from the point
    // estimate + sample size so the level dropdown is live (no re-submit), the same
    // way Cpu/Cpl render. Normal-case only (Bissell is a normal-theory result).
    const nSample = histValues.length;
    const zLevel = Z_BY_CONFIDENCE[confidenceLevel] ?? Z_BY_CONFIDENCE[95];
    const cpkCI = capabilityCI(cpk, nSample, zLevel);
    const ppkCI = capabilityCI(ppk, nSample, zLevel);
    const fmtCI = (ci) => (ci ? `[${fmtIdx(ci.lower)}, ${fmtIdx(ci.upper)}]` : '—');

    // Histogram Values and Labels (using all individual measurements)
    const binCount = 15;
    const dataMin = Math.min(...histValues);
    const dataMax = Math.max(...histValues);

    // Only fold a spec limit into the axis range when it's actually present — a null
    // (one-sided) limit must not drag the axis toward a fabricated 0 (FIX-9 residual).
    const min = Math.min(...[lslF, mean - (3 * std_dev), dataMin].filter(Number.isFinite));
    const max = Math.max(...[uslF, mean + (3 * std_dev), dataMax].filter(Number.isFinite));
    const binWidth = (max - min) / binCount;

    const [histogramValues, , binCenters] = (() => {
        const bins = Array(binCount).fill(0);

        histValues.forEach(value => {
            const binIndex = Math.min(Math.floor((value - min) / binWidth), binCount - 1);
            bins[binIndex]++;
        });

        const edges = Array.from({ length: binCount + 1 }, (_, i) => min + i * binWidth);

        const labels = edges.slice(0, -1).map((edge, i) =>
            `${edge.toFixed(3)} - ${(edges[i + 1]).toFixed(3)}`
        );
        const centers = edges.slice(0, -1).map((edge, i) => edge + binWidth / 2);

        return [bins, labels, centers];
    })();

    // Overall normal curve
    const normalCurveOverall = (() => {
        const numPoints = 100;
        const xValues = Array.from({ length: numPoints }, (_, i) =>
            mean - 4 * std_dev + (i * (8 * std_dev)) / (numPoints - 1)
        );
        const yValues = xValues.map(x => {
            const density = (1 / (std_dev * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * Math.pow((x - mean) / std_dev, 2));
            return density * histValues.length * binWidth;
        });

        return xValues.map((x, i) => ({ x: x, y: yValues[i] }));
    })();

    // Within normal curve (using within_std_dev from moving range or subgroup range)
    const normalCurveWithin = (() => {
        if (!within_std_dev || within_std_dev === 0) return [];

        const numPoints = 100;
        const xValues = Array.from({ length: numPoints }, (_, i) =>
            mean - 4 * within_std_dev + (i * (8 * within_std_dev)) / (numPoints - 1)
        );
        const yValues = xValues.map(x => {
            const density = (1 / (within_std_dev * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * Math.pow((x - mean) / within_std_dev, 2));
            return density * histValues.length * binWidth;
        });

        return xValues.map((x, i) => ({ x: x, y: yValues[i] }));
    })();

    // Selected distribution PDF curve overlay (Phase 4)
    const selectedDistCurve = (() => {
        // Skip if normal is selected (already shown via normalCurveOverall)
        if (selectedDistribution === 'normal') return null;

        // Check if we have a valid fit with a pdf function
        if (!selectedDistFit?.fit?.pdf) return null;

        const numPoints = 100;
        const padding = 0.1 * (max - min);
        const xMin = min - padding;
        const xMax = max + padding;
        const xValues = Array.from({ length: numPoints }, (_, i) =>
            xMin + (i * (xMax - xMin)) / (numPoints - 1)
        );

        try {
            const yValues = xValues.map(x => {
                const density = selectedDistFit.fit.pdf(x);
                // Scale density to match histogram counts
                return (isFinite(density) && density >= 0)
                    ? density * histValues.length * binWidth
                    : 0;
            });

            return xValues.map((x, i) => ({ x: x, y: yValues[i] }));
        } catch {
            return null; // Return null if pdf calculation fails
        }
    })();

    // Chart Data Definitions - Conditional based on chart type
    // Color out-of-control points red
    const controlChartData = isXbarRChart ? {
        // X̄ Chart (Subgroup Means)
        labels: subgroupIds || xbarValues?.map((_, i) => i + 1) || [],
        datasets: [
            {
                label: 'X̄ (Subgroup Mean)',
                data: xbarValues || [],
                borderColor: '#90caf9',
                fill: false,
                pointBackgroundColor: (xbarValues || []).map(v => (v > xbar_ucl || v < xbar_lcl) ? '#ff5252' : '#90caf9'),
                pointBorderColor: (xbarValues || []).map(v => (v > xbar_ucl || v < xbar_lcl) ? '#ff5252' : '#90caf9'),
                pointBorderWidth: 2,
                pointRadius: 5
            },
            // One-sided specs are legal (REQ-CAP-007/008) — omit the USL/LSL dataset
            // entirely when that limit is null instead of drawing a fabricated line at 0.
            uslF !== null && { label: 'USL', data: Array(xbarValues?.length || 0).fill(uslF), borderColor: '#ff5252', borderDash: [5, 5], pointRadius: 0 },
            lslF !== null && { label: 'LSL', data: Array(xbarValues?.length || 0).fill(lslF), borderColor: '#ff5252', borderDash: [5, 5], pointRadius: 0 },
            { label: 'X̄̄ (Grand Mean)', data: Array(xbarValues?.length || 0).fill(xbarBar || mean), borderColor: '#81c784', borderDash: [10, 5], pointRadius: 0 },
            { label: 'X̄ Chart UCL', data: Array(xbarValues?.length || 0).fill(xbar_ucl), borderColor: '#ffb74d', borderDash: [10, 5], pointRadius: 0 },
            { label: 'X̄ Chart LCL', data: Array(xbarValues?.length || 0).fill(xbar_lcl), borderColor: '#ffb74d', borderDash: [10, 5], pointRadius: 0 }
        ].filter(Boolean),
    } : {
        // I Chart (Individual Measurements)
        labels: values.map((_, i) => i + 1),
        datasets: [
            {
                label: 'Measurements',
                data: values,
                borderColor: '#90caf9',
                fill: false,
                pointBackgroundColor: values.map(v => (v > i_ucl || v < i_lcl) ? '#ff5252' : '#90caf9'),
                pointBorderColor: values.map(v => (v > i_ucl || v < i_lcl) ? '#ff5252' : '#90caf9'),
                pointBorderWidth: 2,
                pointRadius: 5
            },
            // One-sided specs are legal (REQ-CAP-007/008) — omit the USL/LSL dataset
            // entirely when that limit is null instead of drawing a fabricated line at 0.
            uslF !== null && { label: 'USL', data: Array(values.length).fill(uslF), borderColor: '#ff5252', borderDash: [5, 5], pointRadius: 0 },
            lslF !== null && { label: 'LSL', data: Array(values.length).fill(lslF), borderColor: '#ff5252', borderDash: [5, 5], pointRadius: 0 },
            { label: 'Mean', data: Array(values.length).fill(mean), borderColor: '#81c784', borderDash: [10, 5], pointRadius: 0 },
            { label: 'I Chart UCL', data: Array(values.length).fill(i_ucl), borderColor: '#ffb74d', borderDash: [10, 5], pointRadius: 0 },
            { label: 'I Chart LCL', data: Array(values.length).fill(i_lcl), borderColor: '#ffb74d', borderDash: [10, 5], pointRadius: 0 }
        ].filter(Boolean),
    };

    const movingRangeData = isXbarRChart ? {
        // R Chart (Subgroup Ranges)
        labels: subgroupIds || rValues?.map((_, i) => i + 1) || [],
        datasets: [
            {
                label: 'R (Subgroup Range)',
                data: rValues || [],
                borderColor: '#90caf9',
                fill: false,
                pointBackgroundColor: (rValues || []).map(r => (r > r_ucl) ? '#ff5252' : '#90caf9'),
                pointBorderColor: (rValues || []).map(r => (r > r_ucl) ? '#ff5252' : '#90caf9'),
                pointBorderWidth: 2,
                pointRadius: 5
            },
            { label: 'R̄ (Mean Range)', data: Array(rValues?.length || 0).fill(rBar), borderColor: '#81c784', borderDash: [10, 5], pointRadius: 0 },
            { label: 'R Chart UCL', data: Array(rValues?.length || 0).fill(r_ucl), borderColor: '#ffb74d', borderDash: [10, 5], pointRadius: 0 },
            { label: 'R Chart LCL', data: Array(rValues?.length || 0).fill(r_lcl), borderColor: '#ffb74d', borderDash: [10, 5], pointRadius: 0 },
        ],
    } : {
        // MR Chart (Moving Ranges)
        labels: movingRanges?.map((_, i) => i + 2) || [],
        datasets: [
            {
                label: 'Moving Range',
                data: movingRanges || [],
                borderColor: '#90caf9',
                fill: false,
                pointBackgroundColor: (movingRanges || []).map(mr => (mr > mr_ucl) ? '#ff5252' : '#90caf9'),
                pointBorderColor: (movingRanges || []).map(mr => (mr > mr_ucl) ? '#ff5252' : '#90caf9'),
                pointBorderWidth: 2,
                pointRadius: 5
            },
            { label: 'MR Mean', data: Array(movingRanges?.length || 0).fill(mrMean), borderColor: '#81c784', borderDash: [10, 5], pointRadius: 0 },
            { label: 'MR UCL', data: Array(movingRanges?.length || 0).fill(mr_ucl), borderColor: '#ffb74d', borderDash: [10, 5], pointRadius: 0 },
        ],
    };

    // Get display name for selected distribution
    const selectedDistName = selectedDistFit?.name || selectedDistribution;

    const histogramData = {
        labels: binCenters,
        datasets: [
            {
                label: 'Frequency',
                data: histogramValues.map((val, i) => ({ x: binCenters[i], y: val })),
                backgroundColor: '#90caf9',
                yAxisID: 'y',
                categoryPercentage: 1.0,
                barPercentage: 1.0,
                barThickness: 'flex',
                order: 3,
            },
            {
                label: 'Normal (Overall σ)',
                data: normalCurveOverall,
                type: 'line',
                borderColor: '#5c6bc0',
                fill: false,
                yAxisID: 'y',
                pointStyle: false,
                tension: 0.4,
                order: 1,
                borderWidth: 2,
            },
            {
                label: 'Normal (Within σ)',
                data: normalCurveWithin,
                type: 'line',
                borderColor: '#81c784',
                fill: false,
                yAxisID: 'y',
                pointStyle: false,
                tension: 0.4,
                order: 2,
                borderWidth: 2,
                borderDash: [5, 5],
            },
            // Selected distribution curve overlay (Phase 4)
            ...(selectedDistCurve ? [{
                label: `${selectedDistName} (Selected)`,
                data: selectedDistCurve,
                type: 'line',
                borderColor: '#ff9800',
                fill: false,
                yAxisID: 'y',
                pointStyle: false,
                tension: 0.4,
                order: 0,
                borderWidth: 2.5,
            }] : []),
        ],
    };

    // Use selected distribution probability plot if available, otherwise fall back to normal
    const useProbPlotData = selectedDistribution !== 'normal' && selectedDistProbPlot
        ? selectedDistProbPlot
        : { theoreticalQuantiles, orderedQuantiles, referenceLine };

    const probabilityPlotData = {
        datasets: (useProbPlotData?.theoreticalQuantiles?.length && useProbPlotData?.orderedQuantiles?.length) ? [
            {
                label: 'Ordered Data',
                data: useProbPlotData.theoreticalQuantiles.map((tq, i) => ({ x: tq, y: useProbPlotData.orderedQuantiles[i] })),
                backgroundColor: '#90caf9',
                type: 'scatter',
                order: 0,
            },
            {
                label: 'Reference Line',
                data: useProbPlotData.referenceLine
                    ? useProbPlotData.theoreticalQuantiles.map((tq, i) => ({ x: tq, y: useProbPlotData.referenceLine[i] }))
                    : [],
                borderColor: selectedDistribution === 'normal' ? '#5c6bc0' : '#ff9800',
                type: 'line',
                fill: false,
                pointRadius: 0,
                order: 1,
            },
            // Confidence bands only for normal distribution (from backend)
            ...(selectedDistribution === 'normal' && lowerBand?.length && upperBand?.length ? [
                {
                    label: 'Lower Confidence Band',
                    data: theoreticalQuantiles.map((tq, i) => ({ x: tq, y: lowerBand[i] })),
                    borderColor: 'rgba(255, 183, 77, 0.5)',
                    borderDash: [5, 5],
                    type: 'line',
                    fill: false,
                    pointRadius: 0,
                    order: 2,
                },
                {
                    label: 'Upper Confidence Band',
                    data: theoreticalQuantiles.map((tq, i) => ({ x: tq, y: upperBand[i] })),
                    borderColor: 'rgba(255, 183, 77, 0.5)',
                    borderDash: [5, 5],
                    type: 'line',
                    fill: '-1',
                    backgroundColor: 'rgba(255, 183, 77, 0.1)',
                    pointRadius: 0,
                    order: 3,
                }
            ] : [])
        ] : [],
    };

    // Chart Options - shared layout for consistent spacing across all charts
    const commonOptions = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 10,
                right: 10,
                bottom: 10,
                left: 10
            }
        },
        plugins: {
            legend: {
                position: 'right',
                labels: {
                    color: '#ffffff',
                    boxWidth: 12,
                    padding: 8,
                    font: { size: 11 }
                }
            },
            tooltip: { enabled: true }
        },
    };

    const axisStyles = {
        title: {
            display: true,
            color: '#ffffff',
            font: { size: 12 },
            padding: { top: 8, bottom: 4 }
        },
        ticks: {
            color: '#b0bec5',
            font: { size: 10 },
            maxRotation: 45,
            minRotation: 0
        }
    };

    // Compute y-axis range for probability plot based on selected distribution
    const probPlotYData = [
        ...(useProbPlotData?.orderedQuantiles || []),
        ...(useProbPlotData?.referenceLine || []),
        ...(selectedDistribution === 'normal' ? [...(lowerBand || []), ...(upperBand || [])] : [])
    ].filter(y => y !== null && y !== undefined && isFinite(y));

    const probPlotXData = (useProbPlotData?.theoreticalQuantiles || []).filter(x => x !== null && isFinite(x));

    const yMin = probPlotYData.length > 0 ? Math.min(...probPlotYData) : 0;
    const yMax = probPlotYData.length > 0 ? Math.max(...probPlotYData) : 1;
    const yRange = yMax - yMin;
    const yPadding = Math.max(0.10 * yRange, 0.5 * std_dev, 0.001);

    const xMin = probPlotXData.length > 0 ? Math.min(...probPlotXData) : 0;
    const xMax = probPlotXData.length > 0 ? Math.max(...probPlotXData) : 1;
    const xRange = xMax - xMin;
    const xPadding = Math.max(0.10 * xRange, 0.001);

    const probabilityPlotOptions = {
        ...commonOptions,
        scales: {
            x: {
                ...axisStyles,
                title: { ...axisStyles.title, text: `Theoretical Quantiles (${selectedDistName})` },
                suggestedMin: xMin - xPadding,
                suggestedMax: xMax + xPadding,
            },
            y: {
                ...axisStyles,
                title: { ...axisStyles.title, text: 'Ordered Values' },
                suggestedMin: yMin - yPadding,
                suggestedMax: yMax + yPadding,
            }
        }
    };

    // Dynamic y-axis scale for control chart based on toggle
    // Always include actual data points in the scale
    const actualDataMin = isXbarRChart ? Math.min(...(xbarValues || [])) : Math.min(...values);
    const actualDataMax = isXbarRChart ? Math.max(...(xbarValues || [])) : Math.max(...values);

    // A null (one-sided) limit must drop out of the candidate list, not coerce to 0.
    const baseChartYMin = useSpecLimitsScale ? Math.min(...[lslF, mean - 3 * std_dev].filter(Number.isFinite)) : (isXbarRChart ? xbar_lcl : i_lcl);
    const baseChartYMax = useSpecLimitsScale ? Math.max(...[uslF, mean + 3 * std_dev].filter(Number.isFinite)) : (isXbarRChart ? xbar_ucl : i_ucl);

    // Ensure scale includes all actual data points
    const chartYMin = Math.min(baseChartYMin, actualDataMin);
    const chartYMax = Math.max(baseChartYMax, actualDataMax);
    const chartYPadding = (chartYMax - chartYMin) * 0.05;

    const controlChartOptions = {
        ...commonOptions,
        scales: {
            x: { ...axisStyles, title: { ...axisStyles.title, text: 'Sample Number' } },
            y: {
                ...axisStyles,
                title: { ...axisStyles.title, text: 'Measurement' },
                min: chartYMin - chartYPadding,
                max: chartYMax + chartYPadding
            }
        }
    };
    const movingRangeOptions = { ...commonOptions, scales: { x: { ...axisStyles, title: { ...axisStyles.title, text: 'Sample Number' } }, y: { ...axisStyles, title: { ...axisStyles.title, text: 'Moving Range' } } } };
    const histogramOptions = {
        ...commonOptions,
        scales: {
            x: {
                type: 'linear',
                offset: false,
                grid: {
                    offset: false
                },
                ...axisStyles,
                title: { ...axisStyles.title, text: 'Measurement' },
            },
            y: { type: 'linear', display: true, position: 'left', ...axisStyles, title: { ...axisStyles.title, text: 'Frequency' } },
        },
        plugins: {
            ...commonOptions.plugins,
            annotation: {
                // One-sided specs are legal — only draw the annotation for a limit that
                // is actually present, instead of a fabricated line at 0 (FIX-9 residual).
                annotations: {
                    ...(lslF !== null ? {
                        lsl: {
                            type: 'line',
                            scaleID: 'x',
                            value: lslF,
                            borderColor: '#ff5252',
                            borderWidth: 2,
                            label: {
                                content: `LSL: ${lslF}`,
                                enabled: true,
                                position: 'start',
                                color: '#ffffff'
                            },
                        },
                    } : {}),
                    ...(uslF !== null ? {
                        usl: {
                            type: 'line',
                            scaleID: 'x',
                            value: uslF,
                            borderColor: '#ff5252',
                            borderWidth: 2,
                            label: {
                                content: `USL: ${uslF}`,
                                enabled: true,
                                position: 'end',
                                color: '#ffffff'
                            },
                        },
                    } : {}),
                },
            },
        },
    };

    const handleDistributionClick = (distId) => {
        // Only allow selection of distributions that were successfully fit
        const distResult = distributionFits.results?.find(r => r.id === distId);
        if (distResult && distResult.canFit && distResult.status !== 'fit_failed') {
            // Turn off Auto mode when manually selecting (Phase 5)
            if (autoModeEnabled) {
                setAutoModeEnabled(false);
            }
            setSelectedDistribution(distId);
        }
    };

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            {/* Distribution Selection Control - Persistent Edge Tab */}
            <Button
                className="no-print"
                onClick={() => setDrawerOpen(true)}
                sx={{
                    position: 'fixed',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    backgroundColor: isNonNormal
                        ? 'rgba(255, 152, 0, 0.7)'
                        : (drawerOpen ? 'rgba(144, 202, 249, 0.3)' : 'rgba(82, 81, 81, 0.7)'),
                    color: '#ffffff',
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    padding: '16px 8px',
                    minWidth: '40px',
                    zIndex: 1200,
                    border: isNonNormal
                        ? '2px solid #ff9800'
                        : (drawerOpen ? '2px solid #90caf9' : '1px solid #666'),
                    '&:hover': {
                        backgroundColor: isNonNormal
                            ? 'rgba(255, 152, 0, 0.8)'
                            : 'rgba(144, 202, 249, 0.4)',
                    },
                }}
                aria-label="Open distribution selection"
            >
                {isNonNormal && '⚠ '}Distribution: {selectedDistribution.charAt(0).toUpperCase() + selectedDistribution.slice(1)}
            </Button>

            {/* Distribution Selection Drawer */}
            <Drawer
                className="no-print"
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 620,
                        backgroundColor: 'rgba(30, 30, 30, 0.98)',
                        color: '#ffffff',
                    },
                }}
            >
                <Box sx={{ p: 2, pt: 1.5, height: '100vh', display: 'flex', flexDirection: 'column' }}>
                    {/* Header - compact */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                                Distribution Identification
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Anderson-Darling GOF at α={ALPHA}
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#ffffff', p: 0.5 }}>
                            <ChevronLeft />
                        </IconButton>
                    </Box>

                    {/* Auto (Recommended) Mode Selector (Phase 5) */}
                    {/* Color coding: green = acceptable fit selected, amber = fallback (no acceptable fits), gray = off */}
                    {(() => {
                        const isFallback = autoModeEnabled && conservativeRec?.isFallback;
                        const bgColor = !autoModeEnabled
                            ? 'rgba(50, 50, 50, 0.5)'
                            : isFallback
                                ? 'rgba(255, 152, 0, 0.15)'
                                : 'rgba(76, 175, 80, 0.15)';
                        const borderColor = !autoModeEnabled
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : isFallback
                                ? '1px solid rgba(255, 152, 0, 0.3)'
                                : '1px solid rgba(76, 175, 80, 0.3)';
                        const buttonBg = !autoModeEnabled
                            ? 'transparent'
                            : isFallback
                                ? '#ff9800'
                                : '#4caf50';
                        const buttonHoverBg = !autoModeEnabled
                            ? 'rgba(255, 255, 255, 0.1)'
                            : isFallback
                                ? '#f57c00'
                                : '#388e3c';

                        return (
                            <Box sx={{
                                mb: 1.5,
                                p: 1,
                                backgroundColor: bgColor,
                                borderRadius: 1,
                                border: borderColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.85rem' }}>
                                        Auto (Recommended)
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem' }}>
                                            {!autoModeEnabled
                                                ? 'Auto-select based on conservative rules'
                                                : isFallback
                                                    ? 'Fallback: least-rejected distribution'
                                                    : 'Using conservative selection rules'
                                            }
                                        </Typography>
                                        <Typography
                                            component={RouterLink}
                                            to="/documentation#distribution-selection"
                                            variant="caption"
                                            sx={{ color: 'rgba(144, 202, 249, 0.8)', fontSize: '0.65rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                        >
                                            Learn more
                                        </Typography>
                                    </Box>
                                </Box>
                                <Button
                                    variant={autoModeEnabled ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => setAutoModeEnabled(!autoModeEnabled)}
                                    sx={{
                                        minWidth: 60,
                                        py: 0.25,
                                        fontSize: '0.75rem',
                                        color: autoModeEnabled ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                        backgroundColor: buttonBg,
                                        '&:hover': {
                                            backgroundColor: buttonHoverBg,
                                            borderColor: 'rgba(255, 255, 255, 0.5)'
                                        }
                                    }}
                                >
                                    {autoModeEnabled ? 'On' : 'Off'}
                                </Button>
                            </Box>
                        );
                    })()}

                    {/* Recommendation Banner (Phase 5 - uses conservative rules) - compact */}
                    {conservativeRec && (
                        <Alert
                            severity={conservativeRec.severity}
                            sx={{
                                mb: 1.5,
                                py: 0.5,
                                '& .MuiAlert-icon': {
                                    color: conservativeRec.severity === 'success' ? '#4caf50'
                                        : conservativeRec.severity === 'warning' ? '#ff9800'
                                        : '#f44336'
                                },
                                '& .MuiAlert-message': { py: 0 }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                                {conservativeRec.text}
                            </Typography>
                        </Alert>
                    )}

                    {/* Data Diagnostics Banner (Phase 1 - preserved) - compact */}
                    {diagnosticChips.length > 0 && (
                        <Box sx={{
                            mb: 1.5,
                            p: 1,
                            backgroundColor: 'rgba(50, 50, 50, 0.7)',
                            borderRadius: 1,
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mr: 0.5, fontSize: '0.65rem' }}>
                                    Diagnostics:
                                </Typography>
                                {diagnosticChips.map((chip) => (
                                    <MuiTooltip
                                        key={chip.id}
                                        title={chip.tooltip}
                                        arrow
                                        placement="bottom"
                                    >
                                        <Chip
                                            label={chip.label}
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontSize: '0.65rem',
                                                backgroundColor: chip.severity === 'warning'
                                                    ? 'rgba(255, 152, 0, 0.2)'
                                                    : 'rgba(144, 202, 249, 0.2)',
                                                color: chip.severity === 'warning'
                                                    ? '#ffb74d'
                                                    : '#90caf9',
                                                border: `1px solid ${chip.severity === 'warning' ? '#ff9800' : '#90caf9'}`,
                                                cursor: 'help'
                                            }}
                                        />
                                    </MuiTooltip>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Distribution Fit Table - flex to fill remaining space */}
                    <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(50, 50, 50, 0.9)', flex: 1, overflow: 'auto' }}>
                        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 0.5 } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', width: '24%', fontSize: '0.7rem' }}>Distribution</TableCell>
                                    <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', textAlign: 'right', fontSize: '0.7rem' }}>AD</TableCell>
                                    <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', textAlign: 'right', fontSize: '0.7rem' }}>P</TableCell>
                                    <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', textAlign: 'right', fontSize: '0.7rem' }}>AIC</TableCell>
                                    <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', textAlign: 'right', fontSize: '0.7rem' }}>ΔAIC</TableCell>
                                    <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', textAlign: 'center', fontSize: '0.7rem' }}>
                                        <MuiTooltip title="Likelihood Ratio Test p-value comparing 3P to 2P model">
                                            <span style={{ cursor: 'help' }}>LRT</span>
                                        </MuiTooltip>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* Group: Acceptable Fits */}
                                {distributionFits.results?.filter(r => r.status === 'acceptable').length > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{
                                            backgroundColor: 'rgba(129, 199, 132, 0.15)',
                                            color: '#81c784',
                                            fontWeight: 'bold',
                                            fontSize: '0.7rem',
                                            py: 0.5
                                        }}>
                                            Acceptable Fits (P ≥ {ALPHA})
                                        </TableCell>
                                    </TableRow>
                                )}
                                {distributionFits.results?.filter(r => r.status === 'acceptable').map((dist) => (
                                    <TableRow
                                        key={dist.id}
                                        onClick={() => handleDistributionClick(dist.id)}
                                        tabIndex={0}
                                        aria-selected={selectedDistribution === dist.id}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDistributionClick(dist.id); } }}
                                        sx={{
                                            cursor: 'pointer',
                                            // Selection styling: neutral (not green/red/yellow)
                                            backgroundColor: selectedDistribution === dist.id
                                                ? 'rgba(255, 255, 255, 0.1)'
                                                : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'rgba(144, 202, 249, 0.12)',
                                            },
                                        }}
                                    >
                                        <TableCell sx={{ color: '#ffffff' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {/* Radio indicator for selection (neutral styling) */}
                                                {selectedDistribution === dist.id
                                                    ? <RadioButtonChecked sx={{ fontSize: 14, color: '#ffffff' }} />
                                                    : <RadioButtonUnchecked sx={{ fontSize: 14, color: '#666' }} />
                                                }
                                                <Typography variant="caption" sx={{
                                                    fontWeight: selectedDistribution === dist.id ? 'bold' : 'normal'
                                                }}>
                                                    {dist.name}
                                                </Typography>
                                                {/* Green indicator for acceptable fit */}
                                                <Box sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#81c784',
                                                    ml: 'auto'
                                                }} />
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#ffffff', textAlign: 'right' }}>
                                            {dist.ad?.toFixed(3) ?? '—'}
                                        </TableCell>
                                        <TableCell sx={{ color: '#81c784', textAlign: 'right', fontWeight: 'bold' }}>
                                            {dist.pValue?.toFixed(3) ?? '—'}
                                        </TableCell>
                                        <TableCell sx={{ color: '#ffffff', textAlign: 'right' }}>
                                            {dist.aic?.toFixed(1) ?? '—'}
                                        </TableCell>
                                        <TableCell sx={{ color: '#ffffff', textAlign: 'right' }}>
                                            {dist.deltaAIC != null ? (dist.deltaAIC < 0.1 ? '0' : dist.deltaAIC.toFixed(1)) : '—'}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {dist.is3P && dist.lrtPValue != null ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="caption" sx={{ color: '#ffffff' }}>
                                                        {dist.lrtPValue.toFixed(3)}
                                                    </Typography>
                                                    <Chip
                                                        label={dist.prefer3P ? '3P' : '2P'}
                                                        size="small"
                                                        sx={{
                                                            height: 16,
                                                            fontSize: '0.6rem',
                                                            backgroundColor: dist.prefer3P
                                                                ? 'rgba(129, 199, 132, 0.3)'
                                                                : 'rgba(255, 183, 77, 0.3)',
                                                            color: dist.prefer3P ? '#81c784' : '#ffb74d',
                                                            border: `1px solid ${dist.prefer3P ? '#81c784' : '#ffb74d'}`,
                                                        }}
                                                    />
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" sx={{ color: '#666' }}>—</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {/* Group: Rejected Fits */}
                                {distributionFits.results?.filter(r => r.status === 'rejected').length > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{
                                            backgroundColor: 'rgba(244, 67, 54, 0.15)',
                                            color: '#f44336',
                                            fontWeight: 'bold',
                                            fontSize: '0.7rem',
                                            py: 0.5
                                        }}>
                                            Rejected Fits (P &lt; {ALPHA})
                                        </TableCell>
                                    </TableRow>
                                )}
                                {distributionFits.results?.filter(r => r.status === 'rejected').map((dist) => (
                                    <TableRow
                                        key={dist.id}
                                        onClick={() => handleDistributionClick(dist.id)}
                                        tabIndex={0}
                                        aria-selected={selectedDistribution === dist.id}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDistributionClick(dist.id); } }}
                                        sx={{
                                            cursor: 'pointer',
                                            backgroundColor: selectedDistribution === dist.id
                                                ? 'rgba(255, 255, 255, 0.1)'
                                                : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'rgba(144, 202, 249, 0.12)',
                                            },
                                        }}
                                    >
                                        <TableCell sx={{ color: '#ffffff' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {selectedDistribution === dist.id
                                                    ? <RadioButtonChecked sx={{ fontSize: 14, color: '#ffffff' }} />
                                                    : <RadioButtonUnchecked sx={{ fontSize: 14, color: '#666' }} />
                                                }
                                                <Typography variant="caption" sx={{
                                                    fontWeight: selectedDistribution === dist.id ? 'bold' : 'normal'
                                                }}>
                                                    {dist.name}
                                                </Typography>
                                                {/* Red indicator for rejected fit */}
                                                <Box sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#f44336',
                                                    ml: 'auto'
                                                }} />
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#ffffff', textAlign: 'right' }}>
                                            {dist.ad?.toFixed(3) ?? '—'}
                                        </TableCell>
                                        <TableCell sx={{ color: '#f44336', textAlign: 'right' }}>
                                            {dist.pValue?.toFixed(3) ?? '—'}
                                        </TableCell>
                                        <TableCell sx={{ color: '#ffffff', textAlign: 'right' }}>
                                            {dist.aic?.toFixed(1) ?? '—'}
                                        </TableCell>
                                        <TableCell sx={{ color: '#ffffff', textAlign: 'right' }}>
                                            {dist.deltaAIC != null ? (dist.deltaAIC < 0.1 ? '0' : dist.deltaAIC.toFixed(1)) : '—'}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {dist.is3P && dist.lrtPValue != null ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="caption" sx={{ color: '#ffffff' }}>
                                                        {dist.lrtPValue.toFixed(3)}
                                                    </Typography>
                                                    <Chip
                                                        label={dist.prefer3P ? '3P' : '2P'}
                                                        size="small"
                                                        sx={{
                                                            height: 16,
                                                            fontSize: '0.6rem',
                                                            backgroundColor: dist.prefer3P
                                                                ? 'rgba(129, 199, 132, 0.3)'
                                                                : 'rgba(255, 183, 77, 0.3)',
                                                            color: dist.prefer3P ? '#81c784' : '#ffb74d',
                                                            border: `1px solid ${dist.prefer3P ? '#81c784' : '#ffb74d'}`,
                                                        }}
                                                    />
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" sx={{ color: '#666' }}>—</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {/* Group: Unstable Fits (3P with threshold too close to data minimum) */}
                                {distributionFits.results?.filter(r => r.status === 'unstable').length > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{
                                            backgroundColor: 'rgba(255, 152, 0, 0.15)',
                                            color: '#ff9800',
                                            fontWeight: 'bold',
                                            fontSize: '0.7rem',
                                            py: 0.5
                                        }}>
                                            Unstable Fits (threshold near data minimum)
                                        </TableCell>
                                    </TableRow>
                                )}
                                {distributionFits.results?.filter(r => r.status === 'unstable').map((dist) => (
                                    <TableRow
                                        key={dist.id}
                                        sx={{
                                            cursor: 'default',
                                            opacity: 0.7,
                                        }}
                                    >
                                        <TableCell sx={{ color: '#ff9800' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <RadioButtonUnchecked sx={{ fontSize: 14, color: '#666' }} />
                                                <Typography variant="caption">
                                                    {dist.name}
                                                </Typography>
                                                <MuiTooltip title="Threshold parameter is too close to data minimum, making this fit unreliable.">
                                                    <Chip
                                                        label="Unstable"
                                                        size="small"
                                                        sx={{
                                                            height: 16,
                                                            fontSize: '0.6rem',
                                                            ml: 'auto',
                                                            backgroundColor: 'rgba(255, 152, 0, 0.2)',
                                                            color: '#ff9800',
                                                            border: '1px solid #ff9800',
                                                        }}
                                                    />
                                                </MuiTooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#888', textAlign: 'right' }}>
                                            {dist.ad?.toFixed(3) ?? '—'}
                                        </TableCell>
                                        <TableCell sx={{ color: '#888', textAlign: 'right' }}>
                                            {dist.pValue?.toFixed(3) ?? '—'}
                                        </TableCell>
                                        <TableCell sx={{ color: '#888', textAlign: 'right' }}>
                                            {dist.aic?.toFixed(1) ?? '—'}
                                        </TableCell>
                                        <TableCell sx={{ color: '#888', textAlign: 'right' }}>
                                            {dist.deltaAIC != null ? (dist.deltaAIC < 0.1 ? '0' : dist.deltaAIC.toFixed(1)) : '—'}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            <Typography variant="caption" sx={{ color: '#888' }}>
                                                {dist.lrtPValue?.toFixed(3) ?? '—'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {/* Group: Not Applicable / Fit Failed */}
                                {distributionFits.results?.filter(r => r.status === 'not_applicable' || r.status === 'fit_failed').length > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{
                                            backgroundColor: 'rgba(100, 100, 100, 0.2)',
                                            color: '#888',
                                            fontWeight: 'bold',
                                            fontSize: '0.7rem',
                                            py: 0.5
                                        }}>
                                            Not Applicable
                                        </TableCell>
                                    </TableRow>
                                )}
                                {distributionFits.results?.filter(r => r.status === 'not_applicable' || r.status === 'fit_failed').map((dist) => (
                                    <TableRow
                                        key={dist.id}
                                        sx={{
                                            cursor: 'default',
                                            opacity: 0.5,
                                        }}
                                    >
                                        <TableCell sx={{ color: '#888' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <RadioButtonUnchecked sx={{ fontSize: 14, color: '#444' }} />
                                                <Typography variant="caption">
                                                    {dist.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#666', textAlign: 'right' }}>—</TableCell>
                                        <TableCell sx={{ color: '#666', textAlign: 'right' }}>—</TableCell>
                                        <TableCell sx={{ color: '#666', textAlign: 'right' }}>—</TableCell>
                                        <TableCell sx={{ color: '#666', textAlign: 'right' }}>
                                            <MuiTooltip title={dist.statusReason || 'Cannot fit this distribution'}>
                                                <Typography variant="caption" sx={{ color: '#888', cursor: 'help' }}>
                                                    N/A
                                                </Typography>
                                            </MuiTooltip>
                                        </TableCell>
                                        <TableCell sx={{ color: '#666', textAlign: 'center' }}>—</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Legend / Info - compact */}
                    <Box sx={{ mt: 1.5, p: 1, backgroundColor: 'rgba(50, 50, 50, 0.5)', borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.55)', display: 'block', fontSize: '0.7rem', lineHeight: 1.4 }}>
                            <strong>AD:</strong> Anderson-Darling &nbsp;|&nbsp;
                            <strong>P:</strong> p-value (≥{ALPHA}=acceptable) &nbsp;|&nbsp;
                            <strong>ΔAIC:</strong> vs best &nbsp;|&nbsp;
                            <strong>LRT P:</strong> 3P vs 2P test
                        </Typography>
                    </Box>

                    {/* Shape Diagnostics Section (Phase 4) - shown conditionally */}
                    {showShapeDiagnostics && shapeDiagnostics && (
                        <Box sx={{ mt: 1 }}>
                            <Button
                                onClick={() => setShapeDiagnosticsOpen(!shapeDiagnosticsOpen)}
                                fullWidth
                                size="small"
                                sx={{
                                    justifyContent: 'space-between',
                                    color: '#ffffff',
                                    backgroundColor: 'rgba(50, 50, 50, 0.7)',
                                    borderRadius: 1,
                                    p: 0.75,
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(70, 70, 70, 0.7)',
                                    },
                                }}
                            >
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    Shape Diagnostics
                                </Typography>
                                {shapeDiagnosticsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </Button>
                            <Collapse in={shapeDiagnosticsOpen}>
                                <Box sx={{
                                    p: 1.5,
                                    backgroundColor: 'rgba(40, 40, 40, 0.7)',
                                    borderBottomLeftRadius: 4,
                                    borderBottomRightRadius: 4,
                                }}>
                                    {/* Shape statistics */}
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
                                        <Box sx={{ flex: '1 1 45%' }}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                                                Sample Skewness
                                            </Typography>
                                            <Typography variant="body2">
                                                {shapeDiagnostics.skewness?.toFixed(3) ?? '—'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: '1 1 45%' }}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                                                Excess Kurtosis
                                            </Typography>
                                            <Typography variant="body2">
                                                {shapeDiagnostics.excessKurtosis?.toFixed(3) ?? '—'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: '1 1 45%' }}>
                                            <MuiTooltip title="Bowley (quartile) skewness: (Q3+Q1-2*Q2)/(Q3-Q1). Values near 0 indicate symmetry.">
                                                <Box sx={{ cursor: 'help' }}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                                                        Bowley Skewness
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {shapeDiagnostics.bowleySkewness?.toFixed(3) ?? '—'}
                                                    </Typography>
                                                </Box>
                                            </MuiTooltip>
                                        </Box>
                                        <Box sx={{ flex: '1 1 45%' }}>
                                            <MuiTooltip title="Robust tail ratio: (P95-P5)/(P75-P25). Normal distribution ≈ 2.91.">
                                                <Box sx={{ cursor: 'help' }}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                                                        Tail Ratio (P95-P5)/(IQR)
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {shapeDiagnostics.robustTailRatio?.toFixed(3) ?? '—'}
                                                    </Typography>
                                                </Box>
                                            </MuiTooltip>
                                        </Box>
                                    </Box>

                                    {/* Interpretation nudges */}
                                    {shapeDiagnostics.nudges?.length > 0 && (
                                        <Box sx={{
                                            mt: 1,
                                            pt: 1,
                                            borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                                        }}>
                                            {shapeDiagnostics.nudges.map((nudge, idx) => (
                                                <Alert
                                                    key={idx}
                                                    severity={nudge.severity === 'warning' ? 'warning' : 'info'}
                                                    sx={{
                                                        mb: 0.5,
                                                        py: 0,
                                                        '& .MuiAlert-message': { py: 0.5 }
                                                    }}
                                                >
                                                    <Typography variant="caption">{nudge.text}</Typography>
                                                </Alert>
                                            ))}
                                        </Box>
                                    )}

                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', display: 'block', mt: 1, fontStyle: 'italic' }}>
                                        Note: Shape diagnostics are provided to aid interpretation when results are ambiguous. They do not directly determine distribution ranking.
                                    </Typography>
                                </Box>
                            </Collapse>
                        </Box>
                    )}
                </Box>
            </Drawer>

            <Grid container spacing={2} sx={{ width: '100%' }}>
                {/* Title row with status chips - full width */}
                <Grid item xs={12}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        mb: 0.5,
                        pb: 0.5,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 500 }}>
                            {title ? `Charts: ${title}` : 'Charts'}
                        </Typography>

                        {/* Status chips inline with title */}
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                            <MuiTooltip title={histValues.length >= 30 ? `Sample size: ${histValues.length} (adequate)` : `Sample size: ${histValues.length} (below recommended 30)`}>
                                <Chip
                                    label={`n=${histValues.length}`}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 500,
                                        backgroundColor: histValues.length >= 30 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)',
                                        color: histValues.length >= 30 ? '#81c784' : '#ffb74d',
                                        border: `1px solid ${histValues.length >= 30 ? '#4caf50' : '#ff9800'}`,
                                    }}
                                />
                            </MuiTooltip>
                            <MuiTooltip title={!isNonNormal ? `Normality: AD p-value ${p_value.toFixed(3)} (data appears normal)` : `Normality: AD p-value ${p_value.toFixed(3)} (non-normal data detected) — Click to view distribution options`}>
                                <Chip
                                    label={!isNonNormal ? 'Normal' : 'Non-Normal'}
                                    size="small"
                                    component="button"
                                    aria-label={!isNonNormal ? 'Normal distribution' : 'View distribution options'}
                                    onClick={() => setDrawerOpen(true)}
                                    sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 500,
                                        backgroundColor: !isNonNormal ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)',
                                        color: !isNonNormal ? '#81c784' : '#ffb74d',
                                        border: `1px solid ${!isNonNormal ? '#4caf50' : '#ff9800'}`,
                                        cursor: 'pointer',
                                    }}
                                />
                            </MuiTooltip>
                            {/* Capability verdict — interpretation of the index already on screen
                                (Cpk when normal, else the non-normal Ppk equivalent). AIAG bands. */}
                            {(() => {
                                const govIdx = isNonNormal
                                    ? (capabilityEquivalents?.available ? capabilityEquivalents.Ppk_equiv : null)
                                    : (Number.isFinite(cpk) && cpk !== 0 ? cpk : null);
                                if (govIdx === null || govIdx === undefined || !Number.isFinite(govIdx)) return null;
                                const label = isNonNormal ? 'Ppk(eq)' : 'Cpk';
                                const [verdict, vColor, vBg] = govIdx >= 1.33
                                    ? ['Capable', '#81c784', 'rgba(76, 175, 80, 0.2)']
                                    : govIdx >= 1.0
                                        ? ['Marginal', '#ffb74d', 'rgba(255, 152, 0, 0.2)']
                                        : ['Not Capable', '#f44336', 'rgba(244, 67, 54, 0.2)'];
                                // Stability gate (#3): when out of control, de-emphasize the
                                // verdict (grey) and swap the tooltip to explain the index is
                                // unreliable until the process is stable.
                                const color = isUnstable ? 'rgba(255, 255, 255, 0.55)' : vColor;
                                const bg = isUnstable ? 'rgba(255, 255, 255, 0.08)' : vBg;
                                const tip = isUnstable
                                    ? `Process not in statistical control — ${label} ${govIdx.toFixed(2)} is unreliable until the process is stable.`
                                    : `Capability: ${label} ${govIdx.toFixed(2)} — ${verdict} (AIAG: ≥1.33 capable · ≥1.0 marginal · <1.0 not capable)`;
                                return (
                                    <MuiTooltip title={tip}>
                                        <Chip
                                            label={`${verdict} · ${label} ${govIdx.toFixed(2)}`}
                                            size="small"
                                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, backgroundColor: bg, color, border: `1px solid ${color}`, opacity: isUnstable ? 0.85 : 1 }}
                                        />
                                    </MuiTooltip>
                                );
                            })()}
                            <MuiTooltip title={!isUnstable
                                ? (hasBackendStability ? 'Process stability (Nelson Rules 1–4): in control' : 'Process stability: in control')
                                : (hasBackendStability
                                    ? `Process stability (Nelson Rules 1–4): ${stabilitySignalCount} out-of-control signal(s) on ${isXbarRChart ? 'X̄' : 'I'} chart`
                                    : `Process stability: ${stabilitySignalCount} out-of-control point(s) on ${isXbarRChart ? 'X̄' : 'I'} chart`)}>
                                <Chip
                                    label={!isUnstable ? 'Stable' : `Unstable · ${stabilitySignalCount}`}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 500,
                                        backgroundColor: !isUnstable ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                                        color: !isUnstable ? '#81c784' : '#f44336',
                                        border: `1px solid ${!isUnstable ? '#4caf50' : '#f44336'}`,
                                    }}
                                />
                            </MuiTooltip>
                            <MuiTooltip title={outOfControlMR === 0 ? 'Variability: Consistent' : `Variability: ${outOfControlMR} excessive range point(s) on ${isXbarRChart ? 'R' : 'MR'} chart`}>
                                <Chip
                                    label={outOfControlMR === 0 ? 'Consistent' : `${outOfControlMR} MR Exc`}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 500,
                                        backgroundColor: outOfControlMR === 0 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                                        color: outOfControlMR === 0 ? '#81c784' : '#f44336',
                                        border: `1px solid ${outOfControlMR === 0 ? '#4caf50' : '#f44336'}`,
                                    }}
                                />
                            </MuiTooltip>
                        </Box>

                        {/* Right side: Warnings toggle */}
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                onClick={() => setWarningsExpanded(!warningsExpanded)}
                                role="button"
                                tabIndex={0}
                                aria-expanded={warningsExpanded}
                                aria-label="Toggle warnings"
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setWarningsExpanded(!warningsExpanded); } }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    color: 'rgba(255, 255, 255, 0.6)',
                                    cursor: 'pointer',
                                    '&:hover': { color: 'rgba(255, 255, 255, 0.9)' }
                                }}
                            >
                                <Typography variant="caption">Warnings</Typography>
                                {warningsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </Box>
                        </Box>
                    </Box>

                    {/* Collapsible Warnings Content */}
                    <Collapse in={warningsExpanded}>
                        <Paper sx={{ p: 1.5, mb: 1, backgroundColor: 'rgba(82, 81, 81, 0.35)' }}>
                            <Box sx={{ maxHeight: '120px', overflowY: 'auto' }}>
                                {!isNonNormal && outOfControlI === 0 && outOfControlMR === 0 && histValues.length >= 30 && (
                                    <Alert severity="success" sx={{ py: 0.5 }}>
                                        No warnings detected. Data appears normal and process is in statistical control.
                                    </Alert>
                                )}
                                {histValues.length < 30 && (
                                    <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                                        <strong>Small Sample Size:</strong> {histValues.length} samples (recommended: 30+).
                                    </Alert>
                                )}
                                {isNonNormal && (
                                    <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                                        <strong>Non-Normal Data:</strong> AD p-value {p_value.toFixed(3)} &lt; 0.05.
                                    </Alert>
                                )}
                                {outOfControlI > 0 && (
                                    <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                                        <strong>Process Not Stable:</strong> {outOfControlI} out-of-control point(s).
                                    </Alert>
                                )}
                                {outOfControlMR > 0 && (
                                    <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                                        <strong>Excessive Variability:</strong> {outOfControlMR} excessive range point(s).
                                    </Alert>
                                )}
                            </Box>
                        </Paper>
                    </Collapse>
                </Grid>

                {/* Charts Column - left on desktop, second on mobile */}
                <Grid item xs={12} lg={9} sx={{ order: { xs: 1, lg: 0 } }}>
                    <Grid container spacing={1.5}>
                        {/* Chart 1: I/X̄ Chart */}
                        <Grid item xs={12} md={6} sx={{ height: '340px' }}>
                            <Paper sx={{ position: 'relative', padding: '10px 12px 12px', height: '100%', width: '100%', backgroundColor: 'rgba(82, 81, 81, 0.35)', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.25, flexShrink: 0 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                        {isXbarRChart ? 'X̄ Chart' : 'I Chart'}
                                    </Typography>
                                    <MuiTooltip title="Shewhart control charts are robust for special-cause detection. The nominal false-alarm rate assumes normality, but charts remain effective for detecting process shifts regardless of distribution." arrow placement="top">
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', cursor: 'help', fontSize: '0.65rem' }}>
                                            (Shewhart)
                                        </Typography>
                                    </MuiTooltip>
                                </Box>
                                <IconButton
                                    onClick={() => setUseSpecLimitsScale(!useSpecLimitsScale)}
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                        color: useSpecLimitsScale ? '#ffffff' : '#616161',
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
                                        zIndex: 10,
                                        p: 0.5
                                    }}
                                    title={useSpecLimitsScale ? 'Scale: Spec Limits' : 'Scale: Control Limits'}
                                >
                                    {useSpecLimitsScale ? <ZoomOutMap fontSize="small" /> : <CenterFocusStrong fontSize="small" />}
                                </IconButton>
                                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                                    <Line data={controlChartData} options={controlChartOptions} role="img" aria-label={`${isXbarRChart ? 'X-bar' : 'Individuals'} control chart, ${histValues.length} points, ${outOfControlI} out of control`} />
                                </Box>
                            </Paper>
                        </Grid>
                        {/* Chart 2: Histogram */}
                        <Grid item xs={12} md={6} sx={{ height: '340px' }}>
                            <Paper sx={{ padding: '10px 12px 12px', height: '100%', width: '100%', backgroundColor: 'rgba(82, 81, 81, 0.35)', display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, textAlign: 'center', mb: 0.25, flexShrink: 0 }}>Histogram</Typography>
                                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                                    <Bar data={histogramData} options={histogramOptions} role="img" aria-label={`Histogram of ${histValues.length} measurements, ${isNonNormal ? 'non-normal' : 'normal'}, AD p-value ${p_value.toFixed(3)}`} />
                                </Box>
                            </Paper>
                        </Grid>
                        {/* Chart 3: MR/R Chart */}
                        <Grid item xs={12} md={6} sx={{ height: '340px' }}>
                            <Paper sx={{ padding: '10px 12px 12px', height: '100%', width: '100%', backgroundColor: 'rgba(82, 81, 81, 0.35)', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.25, flexShrink: 0 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                        {isXbarRChart ? 'R Chart' : 'MR Chart'}
                                    </Typography>
                                    <MuiTooltip title="Shewhart control charts are robust for special-cause detection. The nominal false-alarm rate assumes normality, but charts remain effective for detecting process shifts regardless of distribution." arrow placement="top">
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', cursor: 'help', fontSize: '0.65rem' }}>
                                            (Shewhart)
                                        </Typography>
                                    </MuiTooltip>
                                </Box>
                                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                                    <Line data={movingRangeData} options={movingRangeOptions} role="img" aria-label={`${isXbarRChart ? 'Range' : 'Moving range'} chart, ${outOfControlMR} excessive-range points`} />
                                </Box>
                            </Paper>
                        </Grid>
                        {/* Chart 4: Probability Plot */}
                        <Grid item xs={12} md={6} sx={{ height: '340px' }}>
                            <Paper sx={{ padding: '10px 12px 12px', height: '100%', width: '100%', backgroundColor: 'rgba(82, 81, 81, 0.35)', display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, textAlign: 'center', mb: 0.25, flexShrink: 0 }}>{selectedDistName} Prob. Plot</Typography>
                                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                                    <Scatter data={probabilityPlotData} options={probabilityPlotOptions} role="img" aria-label={`${selectedDistName} probability plot`} />
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Metrics Sidebar - right on desktop, first on mobile */}
                <Grid item xs={12} lg={3} sx={{ order: { xs: 0, lg: 1 } }}>
                    <Paper sx={{ padding: '12px', backgroundColor: 'rgba(82, 81, 81, 0.35)', height: '100%' }}>
                        {/* Stability-before-capability gate (#3): AIAG/ISO require statistical
                            control before Cp/Cpk are trustworthy. When the control chart is out
                            of control, warn (action-first) and de-emphasize the indices below. */}
                        {isUnstable && (
                            <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: '0.72rem', alignItems: 'center', '& .MuiAlert-message': { py: 0.25 } }}>
                                <strong>Establish statistical control</strong> before interpreting Cp/Cpk — {stabilitySignalCount} out-of-control signal{stabilitySignalCount === 1 ? '' : 's'} detected.
                            </Alert>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: isUnstable ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                            {/* NORMAL SELECTED: Show classical Cp/Cpk/Pp/Ppk */}
                            {selectedDistribution === 'normal' && (
                                <>
                                    {/* Confidence-level selector for the Cpk/Ppk CIs (#4) */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mb: -0.5 }}>
                                        <Typography component="label" htmlFor="capability-ci-level" variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.68rem' }}>
                                            CI confidence
                                        </Typography>
                                        <Box
                                            component="select"
                                            id="capability-ci-level"
                                            aria-label="Capability confidence-interval level"
                                            value={confidenceLevel}
                                            onChange={(e) => handleConfidenceChange(parseInt(e.target.value, 10))}
                                            sx={{
                                                backgroundColor: 'rgba(255,255,255,0.06)',
                                                color: 'rgba(255,255,255,0.9)',
                                                border: '1px solid rgba(255,255,255,0.2)',
                                                borderRadius: '4px',
                                                fontSize: '0.7rem',
                                                padding: '1px 4px',
                                                cursor: 'pointer',
                                                '&:hover': { borderColor: 'rgba(255,255,255,0.4)' },
                                            }}
                                        >
                                            <option value={90} style={{ backgroundColor: '#1e242c' }}>90%</option>
                                            <option value={95} style={{ backgroundColor: '#1e242c' }}>95%</option>
                                            <option value={99} style={{ backgroundColor: '#1e242c' }}>99%</option>
                                        </Box>
                                    </Box>
                                    {/* Capability Section (Within) */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: 'rgba(140, 190, 140, 0.95)', fontWeight: 'bold', mb: 0.5, fontSize: '0.8rem' }}>
                                            Within (Cp/Cpk)
                                        </Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, fontSize: '0.75rem' }}>
                                            <Typography variant="body2">
                                                <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Cp:</Box>{' '}
                                                <Box component="span" sx={{ fontWeight: 600 }}>{Number.isFinite(cp) ? cp.toFixed(3) : '—'}</Box>
                                            </Typography>
                                            <Typography variant="body2">
                                                <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Cpk:</Box>{' '}
                                                <Box component="span" sx={{ fontWeight: 600 }}>{cpk.toFixed(3)}</Box>
                                            </Typography>
                                            <MuiTooltip title="CPU = (USL − mean) / 3σ (upper side)">
                                                <Typography variant="body2" sx={{ cursor: 'help' }}>
                                                    <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Cpu:</Box>{' '}
                                                    <Box component="span" sx={{ fontWeight: 600 }}>{fmtIdx(pickIdx(cpu, uslF, mean, within_std_dev, true))}</Box>
                                                </Typography>
                                            </MuiTooltip>
                                            <MuiTooltip title="CPL = (mean − LSL) / 3σ (lower side); the smaller of Cpu/Cpl drives Cpk">
                                                <Typography variant="body2" sx={{ cursor: 'help' }}>
                                                    <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Cpl:</Box>{' '}
                                                    <Box component="span" sx={{ fontWeight: 600 }}>{fmtIdx(pickIdx(cpl, lslF, mean, within_std_dev, false))}</Box>
                                                </Typography>
                                            </MuiTooltip>
                                            <Typography variant="body2">
                                                <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>σ:</Box>{' '}
                                                <Box component="span" sx={{ fontWeight: 600 }}>{within_std_dev.toFixed(4)}</Box>
                                            </Typography>
                                            <Typography variant="body2">
                                                <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>PPM:</Box>{' '}
                                                <Box component="span" sx={{ fontWeight: 600 }}>{ppm_within.toFixed(0)}</Box>
                                            </Typography>
                                        </Box>
                                        <MuiTooltip title={`${confidenceLevel}% confidence interval for Cpk (Bissell 1990 normal approximation, n=${nSample}).`}>
                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'rgba(255,255,255,0.45)', fontSize: '0.68rem', cursor: 'help' }}>
                                                Cpk {confidenceLevel}% CI: <Box component="span" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>{fmtCI(cpkCI)}</Box>
                                            </Typography>
                                        </MuiTooltip>
                                    </Box>

                                    {/* Overall Capability Section */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: 'rgba(130, 145, 180, 0.95)', fontWeight: 'bold', mb: 0.5, fontSize: '0.8rem' }}>
                                            Overall (Pp/Ppk)
                                        </Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                                            <Typography variant="body2">
                                                <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Pp:</Box>{' '}
                                                <Box component="span" sx={{ fontWeight: 600 }}>{Number.isFinite(pp) ? pp.toFixed(3) : '—'}</Box>
                                            </Typography>
                                            <Typography variant="body2">
                                                <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Ppk:</Box>{' '}
                                                <Box component="span" sx={{ fontWeight: 600 }}>{ppk.toFixed(3)}</Box>
                                            </Typography>
                                            <MuiTooltip title="PPU = (USL − mean) / 3σ_overall (upper side)">
                                                <Typography variant="body2" sx={{ cursor: 'help' }}>
                                                    <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Ppu:</Box>{' '}
                                                    <Box component="span" sx={{ fontWeight: 600 }}>{fmtIdx(pickIdx(ppu, uslF, mean, std_dev, true))}</Box>
                                                </Typography>
                                            </MuiTooltip>
                                            <MuiTooltip title="PPL = (mean − LSL) / 3σ_overall (lower side); the smaller of Ppu/Ppl drives Ppk">
                                                <Typography variant="body2" sx={{ cursor: 'help' }}>
                                                    <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Ppl:</Box>{' '}
                                                    <Box component="span" sx={{ fontWeight: 600 }}>{fmtIdx(pickIdx(ppl, lslF, mean, std_dev, false))}</Box>
                                                </Typography>
                                            </MuiTooltip>
                                            <Typography variant="body2">
                                                <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>σ:</Box>{' '}
                                                <Box component="span" sx={{ fontWeight: 600 }}>{std_dev.toFixed(4)}</Box>
                                            </Typography>
                                            <Typography variant="body2">
                                                <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>PPM:</Box>{' '}
                                                <Box component="span" sx={{ fontWeight: 600 }}>{ppm_overall.toFixed(0)}</Box>
                                            </Typography>
                                        </Box>
                                        <MuiTooltip title={`${confidenceLevel}% confidence interval for Ppk (Bissell 1990 normal approximation, n=${nSample}).`}>
                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'rgba(255,255,255,0.45)', fontSize: '0.68rem', cursor: 'help' }}>
                                                Ppk {confidenceLevel}% CI: <Box component="span" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>{fmtCI(ppkCI)}</Box>
                                            </Typography>
                                        </MuiTooltip>
                                    </Box>
                                </>
                            )}

                            {/* NON-NORMAL SELECTED: Show distribution-based capability equivalents */}
                            {selectedDistribution !== 'normal' && (
                                <Box>
                                    <MuiTooltip title="Tail-based method: maps P(OOS) to equivalent normal Z-score, then Ppk = Z/3">
                                        <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'bold', mb: 0.5, cursor: 'help', fontSize: '0.8rem' }}>
                                            {selectedDistName} Equiv.
                                        </Typography>
                                    </MuiTooltip>
                                    {capabilityEquivalents?.available ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {capabilityEquivalents.Pp_equiv_zbench != null && (
                                                <MuiTooltip title="Minitab-parity Pp (Z-bench): mean of the tail-based PPU/PPL indices. Matches Minitab's non-normal Pp.">
                                                    <Typography variant="body2" sx={{ cursor: 'help' }}>
                                                        <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Pp(eq):</Box>{' '}
                                                        <Box component="span" sx={{ fontWeight: 600 }}>{capabilityEquivalents.Pp_equiv_zbench.toFixed(3)}</Box>
                                                    </Typography>
                                                </MuiTooltip>
                                            )}
                                            {capabilityEquivalents.Pp_equiv != null && (
                                                <MuiTooltip title={`ISO percentile method: (USL - LSL) / process spread, spread = ${capabilityEquivalents.spread?.toFixed(4) ?? 'N/A'}. Diverges from Minitab for skewed fits.`}>
                                                    <Typography variant="body2" sx={{ cursor: 'help' }}>
                                                        <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>Pp(ISO):</Box>{' '}
                                                        <Box component="span" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.7)' }}>{capabilityEquivalents.Pp_equiv.toFixed(3)}</Box>
                                                    </Typography>
                                                </MuiTooltip>
                                            )}
                                            {capabilityEquivalents.Ppk_equiv !== null && (
                                                <MuiTooltip title={`Tail-based: Z/3 where Z = min(${capabilityEquivalents.Z_lower?.toFixed(3) ?? 'N/A'}, ${capabilityEquivalents.Z_upper?.toFixed(3) ?? 'N/A'})`}>
                                                    <Typography variant="body2" sx={{ cursor: 'help' }}>
                                                        <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Ppk(eq):</Box>{' '}
                                                        <Box component="span" sx={{ fontWeight: 600 }}>{capabilityEquivalents.Ppk_equiv.toFixed(3)}</Box>
                                                    </Typography>
                                                </MuiTooltip>
                                            )}
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                                                [{capabilityEquivalents.sidedness}]
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
                                            Unavailable
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {/* Practical Impact */}
                            {tailProbability?.hasLimits && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: 'rgba(220, 170, 100, 0.95)', fontWeight: 'bold', mb: 0.5, fontSize: '0.8rem' }}>
                                        Practical Impact
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <MuiTooltip title={`P(Below LSL) = ${(tailProbability.pLower * 100).toFixed(4)}%, P(Above USL) = ${(tailProbability.pUpper * 100).toFixed(4)}%`}>
                                            <Typography variant="body2" sx={{ cursor: 'help' }}>
                                                <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>P(OOS):</Box>{' '}
                                                <Box component="span" sx={{ fontWeight: 600 }}>{(tailProbability.probability * 100).toFixed(4)}%</Box>
                                            </Typography>
                                        </MuiTooltip>
                                        <Typography variant="body2">
                                            <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>PPM:</Box>{' '}
                                            <Box component="span" sx={{ fontWeight: 600 }}>{tailProbability.ppm.toFixed(0)}</Box>
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* GOF Statistics */}
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: 'rgba(150, 190, 220, 0.95)', fontWeight: 'bold', mb: 0.5, fontSize: '0.8rem' }}>
                                    Goodness-of-Fit
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                                    <Typography variant="body2">
                                        <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>AD:</Box>{' '}
                                        <Box component="span" sx={{ fontWeight: 600 }}>{selectedDistFit?.ad?.toFixed(3) ?? ad_value.toFixed(3)}</Box>
                                    </Typography>
                                    <Typography variant="body2">
                                        <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>P:</Box>{' '}
                                        <Box component="span" sx={{ fontWeight: 600 }}>{selectedDistFit?.pValue?.toFixed(3) ?? p_value.toFixed(3)}</Box>
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Data Summary - collapsible */}
                            <Box>
                                <Button
                                    onClick={() => setDataSummaryOpen(!dataSummaryOpen)}
                                    size="small"
                                    fullWidth
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.6)',
                                        textTransform: 'none',
                                        fontSize: '0.7rem',
                                        py: 0.25,
                                        justifyContent: 'space-between',
                                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
                                    }}
                                    endIcon={dataSummaryOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                >
                                    Data Summary
                                </Button>
                                <Collapse in={dataSummaryOpen}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5, px: 1, backgroundColor: 'rgba(50, 50, 50, 0.4)', borderRadius: 1, mt: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>n: {histValues.length}</Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Min: {Math.min(...histValues).toFixed(4)}</Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Max: {Math.max(...histValues).toFixed(4)}</Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Mean: {mean.toFixed(4)}</Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>StdDev: {std_dev.toFixed(4)}</Typography>
                                    </Box>
                                </Collapse>
                            </Box>

                            {/* Distribution-specific parameters - shown when non-normal selected */}
                            {selectedDistribution !== 'normal' && distMoments && (
                                <Box sx={{ pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5 }}>
                                        Fitted Parameters
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        {selectedDistribution.includes('lognormal') && (
                                            <>
                                                <Typography variant="caption">μ(log): {distMoments.muLog?.toFixed(4)}</Typography>
                                                <Typography variant="caption">σ(log): {distMoments.sigmaLog?.toFixed(4)}</Typography>
                                                {distMoments.threshold !== undefined && <Typography variant="caption">Thresh: {distMoments.threshold?.toFixed(4)}</Typography>}
                                            </>
                                        )}
                                        {selectedDistribution.includes('weibull') && (
                                            <>
                                                <Typography variant="caption">Shape(β): {distMoments.shape?.toFixed(4)}</Typography>
                                                <Typography variant="caption">Scale(η): {distMoments.scale?.toFixed(4)}</Typography>
                                                {distMoments.threshold !== undefined && <Typography variant="caption">Thresh(γ): {distMoments.threshold?.toFixed(4)}</Typography>}
                                            </>
                                        )}
                                        {selectedDistribution.includes('gamma') && (
                                            <>
                                                <Typography variant="caption">Shape(α): {distMoments.shape?.toFixed(4)}</Typography>
                                                <Typography variant="caption">Rate(β): {distMoments.rate?.toFixed(4)}</Typography>
                                                {distMoments.threshold !== undefined && <Typography variant="caption">Thresh(γ): {distMoments.threshold?.toFixed(4)}</Typography>}
                                            </>
                                        )}
                                        {selectedDistribution === 'exponential' && (
                                            <>
                                                <Typography variant="caption">Rate(λ): {distMoments.rate?.toFixed(4)}</Typography>
                                                <Typography variant="caption">Mean: {distMoments.mean?.toFixed(4)}</Typography>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default ChartSection;
